import Avatar from '@/components/Avatar'
import Button from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import { Stack } from '@/components/Layout'
import MenuLinkExternal from '@/components/MenuLinks'
import { useAuth } from '@/context/auth'
import { useFeatureToggle } from '@/context/featureToggles'
import { getConfig } from '@/lib/config'
import { PrimaryNavMenuItem, UserMenuNavigationItem } from '@/shared/api-generated-types'
import AuthMenu from '@/widgets/AuthMenu'
import UserCard from '@/widgets/UserCard'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useMemo } from 'react'
import { CSSTransition } from 'react-transition-group'
import * as styles from './style'

interface ExternalLink {
  url?: string
  label: string
  openInNewTab: boolean
}

const { publicRuntimeConfig } = getConfig()
const BASE_URL = publicRuntimeConfig.website.url

export interface Props {
  menuVisible: boolean
  close: () => void
  additionalLinks?: PrimaryNavMenuItem[]
  userMenuLinks?: UserMenuNavigationItem[]
}

const SliderMenu = ({ menuVisible, close, additionalLinks, userMenuLinks }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const { externalLinks } = ExternalLinks()
  const canSeeClassNotes = useFeatureToggle('eng_class_notes')
  const {
    state: { user },
    dispatch,
  } = useAuth()
  const isAuthenticated = useMemo(() => {
    if (user) {
      return true
    }
    return false
  }, [user])

  function ExternalLinks(): { externalLinks: ExternalLink[] } {
    const externalLinks = userMenuLinks
      ? [
          ...userMenuLinks.map((link) => ({
            label: link.label,
            url: link.url,
            openInNewTab: link.openInNewTab,
          })),
          { label: t('nav.settings'), url: `${BASE_URL}/account-settings`, openInNewTab: false },
        ]
      : []
    return { externalLinks }
  }

  return (
    <nav>
      <CSSTransition
        in={menuVisible}
        timeout={styles.transitionDelay}
        unmountOnExit
        classNames={`${styles.menuWrapper} sliderMenu`}
      >
        <Stack y>
          <span className={styles.closeMenu} onClick={close}>
            <IcoMoon icon="close" size={24} />
          </span>
          <div className="pb-lg">
            {canSeeClassNotes && (
              <UserCard
                avatar={<Avatar title={user?.fullName} size="sm" />}
                theme="primaryNext"
                title={user?.firstName}
                href="/notes"
                onClick={close}
              />
            )}
            {!isAuthenticated && (
              <img src="/images/logo-getsetup-light.svg" alt="GetSetUp logo" loading="lazy" width="120px" />
            )}
          </div>
          {externalLinks.map((externalLink, i) => (
            <div key={i} className="py-xxs">
              <MenuLinkExternal
                label={externalLink.label}
                url={externalLink.url}
                openInNewTab={externalLink.openInNewTab}
              />
            </div>
          ))}

          {/* My Bookmarks */}
          {isAuthenticated && (
            <Link href="/my-bookmarks" prefetch={false} onClick={close}>
              <span className="flex items-center text-white ml-2 mt-2">{t('nav.myBookmarks')}</span>
            </Link>
          )}

          <div className="pt-lg">
            {!isAuthenticated && <AuthMenu />}
            {isAuthenticated && (
              <Button
                className={styles.logoutButton}
                size="sm"
                theme="secondary"
                label={t('nav.logOut')}
                onClick={() => dispatch({ type: 'LOGOUT' })}
              />
            )}
          </div>
          <div className="flex flex-col absolute bottom-6 left-6 space-y-4">
            {(additionalLinks || []).map((link, i) => (
              <MenuLinkExternal key={i} label={link.label} url={link.url} openInNewTab={link.openInNewTab} />
            ))}
          </div>
        </Stack>
      </CSSTransition>
      <CSSTransition in={menuVisible} timeout={styles.transitionDelay} unmountOnExit classNames="sliderBackdrop">
        <div className={`${styles.backdrop} backDrop`}></div>
      </CSSTransition>
    </nav>
  )
}

export default SliderMenu
